import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { AddPersonLocationDialog, type AddedPersonLocation } from "./AddPersonLocationDialog";
import { SectionHead } from "./person-form/fields";
import { tempKey, type LocationDraft } from "./person-form/personFormSchema";

interface LocationSelectorProps {
  value: LocationDraft[];
  onChange: (locations: LocationDraft[]) => void;
}

/**
 * The person form's locations: a plain list of the person's addresses with
 * "make primary" and remove, plus an "Add location" button that opens
 * `AddPersonLocationDialog`. At most one location per type is primary (the
 * `uq_person_locations_primary_per_type` index); the first location of a type
 * is made primary when added.
 */
export function LocationSelector({ value, onChange }: LocationSelectorProps) {
  const { t } = useTranslation("people");
  const [addOpen, setAddOpen] = useState(false);

  const add = (added: AddedPersonLocation) => {
    const isPrimary = !value.some((loc) => loc.location_type === added.location_type);
    onChange([
      ...value,
      {
        key: tempKey("location"),
        gis_location_id: added.gis_location_id,
        formatted_address: added.formatted_address,
        location_type: added.location_type,
        is_primary: isPrimary,
        notes: added.notes,
      },
    ]);
  };

  const togglePrimary = (key: string) => {
    const target = value.find((loc) => loc.key === key);
    if (!target) return;
    const makePrimary = !target.is_primary;
    onChange(
      value.map((loc) => {
        if (loc.key === key) return { ...loc, is_primary: makePrimary };
        // Making one primary demotes the other primary of the same type.
        if (makePrimary && loc.location_type === target.location_type) return { ...loc, is_primary: false };
        return loc;
      }),
    );
  };

  const remove = (key: string) => onChange(value.filter((loc) => loc.key !== key));

  return (
    <section>
      <div className="flex items-center justify-between gap-4">
        <SectionHead className="mb-0">{t("form.sections.locations")}</SectionHead>
        <Button type="button" variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("form.location.addButton")}
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-3">{t("form.location.empty")}</p>
      ) : (
        <ul className="mt-3 divide-y border rounded-lg bg-background">
          {value.map((loc) => (
            <li key={loc.key} className="flex items-start justify-between gap-4 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium break-words">{loc.formatted_address || "—"}</p>
                <p className="text-xs text-muted-foreground">
                  {[
                    t(`common:people.locationTypes.${loc.location_type}`, { defaultValue: loc.location_type }),
                    loc.is_primary ? t("common:people.primary") : null,
                    loc.notes || null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button type="button" variant="ghost" size="sm" onClick={() => togglePrimary(loc.key)}>
                  {loc.is_primary ? t("form.location.removePrimary") : t("form.location.makePrimary")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={t("form.location.remove", { address: loc.formatted_address })}
                  onClick={() => remove(loc.key)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {addOpen && <AddPersonLocationDialog open={addOpen} onOpenChange={setAddOpen} onAdd={add} />}
    </section>
  );
}
